import React, { useEffect, lazy } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { Button } from '../ui/Button';
// Define case studies data
const caseStudiesData = {
  'cx-feature-revamp': {
    title: 'AI-Powered CX Feature',
    subtitle: 'Complex module redefined with AI-driven insights and prototype in 2 weeks',
    client: 'AdTech Enterprise',
    duration: '2 weeks',
    year: '2023',
    tags: ['Senior PM', 'AI Prototype', 'AdTech / CX'],
    gradient: 'from-amber-400 via-orange-500 to-amber-300',
    image: 'https://images.unsplash.com/photo-1581287053822-fd7bf4f4bfec?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80&fm=webp',
    images: ['https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80&fm=webp', 'https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80&fm=webp'],
    overview: 'We worked with a leading AdTech enterprise to reimagine their customer experience feature using AI-powered insights. The client needed to modernize their approach to customer feedback and service delivery while maintaining compatibility with their existing systems.',
    challenge: "The client's legacy CX module was complex and difficult to use, leading to low adoption rates and poor customer satisfaction scores. They needed a solution that would simplify the user experience while adding powerful new capabilities powered by AI.",
    approach: ['Conducted AI-powered analysis of customer feedback and support tickets', 'Developed user personas and journey maps to identify pain points', 'Created rapid prototypes using AI design tools', 'Tested prototypes with real users and iterated based on feedback', 'Developed a comprehensive implementation plan with phased rollout'],
    solution: 'We designed an AI-powered CX module that integrated seamlessly with their existing systems while providing a modern, intuitive interface. The solution included natural language processing for customer feedback analysis, automated response suggestions, and a streamlined workflow for support agents.',
    results: ['42% increase in agent productivity', '28% improvement in customer satisfaction scores', '35% reduction in time to resolution', 'Successful integration with existing systems with zero downtime']
  },
  'ai-feedback-app': {
    title: 'LLM Feedback App (0→1)',
    subtitle: 'Voice/chat captures with AI analysis turned into actionable reviews in 3 weeks',
    client: 'HR Tech Startup',
    duration: '3 weeks',
    year: '2023',
    tags: ['Senior PM', 'AI Integration', 'HR Tech'],
    gradient: 'from-blue-600 via-indigo-500 to-purple-500',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80&fm=webp',
    images: ['https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80&fm=webp', 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80&fm=webp'],
    overview: 'We partnered with an innovative HR Tech startup to build a feedback application from scratch that leverages LLMs to transform voice and chat conversations into structured, actionable performance reviews.',
    challenge: 'Traditional performance reviews are time-consuming, subjective, and often fail to capture meaningful feedback. The client wanted to create a revolutionary approach that would make continuous feedback easy while providing deeper insights.',
    approach: ['Conducted market research to validate the concept with potential users', 'Designed the AI architecture for processing natural language input', 'Created a flexible data model for storing and analyzing feedback', 'Developed rapid prototypes to test different user interfaces', 'Conducted user testing with HR professionals and managers'],
    solution: 'We built a feedback application that captures voice and chat conversations, uses LLMs to analyze the content for sentiment, topics, and actionable insights, and then generates structured performance reviews that can be edited and shared with team members.',
    results: ['75% reduction in time spent on performance reviews', '3x increase in the frequency of feedback exchanges', '89% of users reported more meaningful feedback', 'Successfully raised $2.5M in seed funding based on the prototype']
  },
  'screening-platform-revamp': {
    title: 'ML Recommendation Engine',
    subtitle: 'AI-powered platform with rapid prototyping and 30% engagement increase',
    client: 'E-commerce Platform',
    duration: '4 weeks',
    year: '2023',
    tags: ['Lead PM', 'ML Algorithm', 'E-commerce'],
    gradient: 'from-orange-500 via-red-500 to-pink-500',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80&fm=webp',
    images: ['https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80&fm=webp', 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80&fm=webp'],
    overview: 'We helped a growing e-commerce platform develop a sophisticated machine learning recommendation engine to increase user engagement and drive sales through personalized product suggestions.',
    challenge: "The client's existing recommendation system was based on simple rules and wasn't delivering the level of personalization needed to compete with larger platforms. They needed a more advanced solution that could learn from user behavior and adapt over time.",
    approach: ['Analyzed existing user data to identify patterns and opportunities', 'Designed a hybrid recommendation architecture combining collaborative filtering and content-based approaches', 'Created a prototype that could be tested with a subset of users', 'Implemented A/B testing framework to measure performance', 'Developed a roadmap for continuous improvement and feature expansion'],
    solution: 'We built a machine learning recommendation engine that analyzes user behavior, purchase history, and product attributes to deliver highly personalized recommendations. The system continuously learns and improves based on user interactions, and includes features for managing cold-start problems with new users and products.',
    results: ['30% increase in user engagement with recommended products', '24% increase in average order value', '18% improvement in conversion rate', 'Scalable architecture supporting millions of users and products']
  }
};
export const CaseStudyDetail = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Get case study data based on ID
  const caseStudy = id ? caseStudiesData[id as keyof typeof caseStudiesData] : null;
  if (!caseStudy) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-53">
        <div className="text-center" data-id="element-54">
          <h1 className="text-2xl font-bold mb-4" data-id="element-55">Case Study Not Found</h1>
          <Link to="/" className="text-primary hover:underline" data-id="element-56">
            Return to Home
          </Link>
        </div>
      </div>;
  }
  const openCalendly = () => {
    window.open('https://calendly.com/zerototendigital/exploration-feature-design', '_blank');
  };
  return <div className="bg-white min-h-screen pt-20" data-id="element-57">
      {/* Hero Section */}
      <div className={`bg-gradient-to-br ${caseStudy.gradient} py-20 relative overflow-hidden`} data-id="element-58">
        <div className="absolute inset-0 bg-black/10" data-id="element-59"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-60">
          <Link to="/" className="inline-flex items-center text-white bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-8 hover:bg-white/30 transition-colors" data-id="element-61">
            <ArrowLeftIcon size={16} className="mr-2" data-id="element-62" />
            Back to Home
          </Link>
          <div className="max-w-3xl" data-id="element-63">
            <div className="flex flex-wrap gap-2 mb-6" data-id="element-64">
              {caseStudy.tags.map((tag, index) => <span key={index} className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium" data-id="element-65">
                  {tag}
                </span>)}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-66">
              {caseStudy.title}
            </h1>
            <p className="text-xl text-white/90 mb-8" data-id="element-67">{caseStudy.subtitle}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-white/90" data-id="element-68">
              <div data-id="element-69">
                <h3 className="text-white font-semibold mb-1" data-id="element-70">Client</h3>
                <p data-id="element-71">{caseStudy.client}</p>
              </div>
              <div data-id="element-72">
                <h3 className="text-white font-semibold mb-1" data-id="element-73">Duration</h3>
                <p data-id="element-74">{caseStudy.duration}</p>
              </div>
              <div data-id="element-75">
                <h3 className="text-white font-semibold mb-1" data-id="element-76">Year</h3>
                <p data-id="element-77">{caseStudy.year}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-6 py-16" data-id="element-78">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16" data-id="element-79">
          {/* Sidebar */}
          <div className="lg:col-span-1" data-id="element-80">
            <div className="sticky top-24" data-id="element-81">
              <h3 className="text-xl font-bold mb-6" data-id="element-82">Case Study Sections</h3>
              <nav className="space-y-4" data-id="element-83">
                <a href="#overview" className="block text-text-secondary hover:text-primary transition-colors" data-id="element-84">
                  Overview
                </a>
                <a href="#challenge" className="block text-text-secondary hover:text-primary transition-colors" data-id="element-85">
                  Challenge
                </a>
                <a href="#approach" className="block text-text-secondary hover:text-primary transition-colors" data-id="element-86">
                  Our Approach
                </a>
                <a href="#solution" className="block text-text-secondary hover:text-primary transition-colors" data-id="element-87">
                  Solution
                </a>
                <a href="#results" className="block text-text-secondary hover:text-primary transition-colors" data-id="element-88">
                  Results
                </a>
              </nav>
              <div className="mt-12 p-6 bg-gray-50 rounded-lg" data-id="element-89">
                <h3 className="text-lg font-semibold mb-4" data-id="element-90">
                  Need similar results?
                </h3>
                <p className="text-text-secondary mb-6" data-id="element-91">
                  Let's discuss how our product management services can help
                  your business.
                </p>
                <Button onClick={openCalendly} variant="primary" className="w-full" data-id="element-92">
                  Speak with a Senior PM
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2" data-id="element-93">
            <div className="mb-16" data-id="element-94">
              <img src={caseStudy.image} alt={caseStudy.title} className="w-full h-[400px] object-cover rounded-2xl shadow-lg mb-8" data-id="element-95" />
            </div>
            <section id="overview" className="mb-16" data-id="element-96">
              <h2 className="text-3xl font-bold mb-6" data-id="element-97">Overview</h2>
              <p className="text-lg text-text-secondary leading-relaxed" data-id="element-98">
                {caseStudy.overview}
              </p>
            </section>
            <section id="challenge" className="mb-16" data-id="element-99">
              <h2 className="text-3xl font-bold mb-6" data-id="element-100">The Challenge</h2>
              <p className="text-lg text-text-secondary leading-relaxed mb-8" data-id="element-101">
                {caseStudy.challenge}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-102">
                {caseStudy.images.map((image, index) => <img key={index} src={image} alt={`${caseStudy.title} process ${index + 1}`} className="w-full h-64 object-cover rounded-lg shadow-md" loading="lazy" data-id="element-103" />)}
              </div>
            </section>
            <section id="approach" className="mb-16" data-id="element-104">
              <h2 className="text-3xl font-bold mb-6" data-id="element-105">Our Approach</h2>
              <p className="text-lg text-text-secondary leading-relaxed mb-8" data-id="element-106">
                We implemented a structured product management approach to
                address the challenges:
              </p>
              <ul className="space-y-4" data-id="element-107">
                {caseStudy.approach.map((item, index) => <li key={index} className="flex items-start" data-id="element-108">
                    <CheckCircleIcon className="text-primary mt-1 mr-3 flex-shrink-0" size={20} data-id="element-109" />
                    <span className="text-lg text-text-secondary" data-id="element-110">{item}</span>
                  </li>)}
              </ul>
            </section>
            <section id="solution" className="mb-16" data-id="element-111">
              <h2 className="text-3xl font-bold mb-6" data-id="element-112">The Solution</h2>
              <p className="text-lg text-text-secondary leading-relaxed" data-id="element-113">
                {caseStudy.solution}
              </p>
            </section>
            <section id="results" className="mb-16" data-id="element-114">
              <h2 className="text-3xl font-bold mb-6" data-id="element-115">Results</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-116">
                {caseStudy.results.map((result, index) => <div key={index} className={`p-6 rounded-xl bg-gradient-to-br ${caseStudy.gradient} text-white`} data-id="element-117">
                    <p className="text-xl font-bold" data-id="element-118">{result}</p>
                  </div>)}
              </div>
            </section>
            <div className="border-t border-gray-200 pt-12" data-id="element-119">
              <h2 className="text-2xl font-bold mb-6" data-id="element-120">
                Ready to transform your product strategy?
              </h2>
              <p className="text-lg text-text-secondary mb-8" data-id="element-121">
                Our product management services can help you achieve similar
                results. Let's discuss your project.
              </p>
              <Button onClick={openCalendly} variant="primary" className="text-lg px-8" data-id="element-122">
                Speak with a Senior PM
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};